import { LanguageSelector } from '@/components/common';
import { ThemeToggle } from '@/components/theme';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarFooter as SidebarFooterPrimitive,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { useAuth } from '@/hooks/useAuth';
import { useTranslation } from '@/hooks/useTranslation';
import { ChevronUp } from 'lucide-react';
import { BOTTOM_NAVIGATION_ITEMS } from './constants';
import { NavigationItem } from './NavigationItem';
import { SidebarVersionInfo } from './SidebarVersionInfo';
import { useLogout } from './useLogout';
import { getUserInitials } from './utils';

interface SidebarFooterProps {
  isActive: (path: string) => boolean;
}

export const SidebarFooter = ({ isActive }: SidebarFooterProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { handleLogout } = useLogout();

  return (
    <SidebarFooterPrimitive>
      {/* Bottom navigation items. Same structure as the main nav
          (SidebarMenu with menu buttons) so icons center identically when
          the sidebar collapses to the icon rail. */}
      <SidebarMenu className='mt-auto'>
        {BOTTOM_NAVIGATION_ITEMS.map(item => (
          <NavigationItem key={item.name} item={item} isActive={isActive} />
        ))}
      </SidebarMenu>

      {/* User menu */}
      <SidebarMenu>
        <SidebarMenuItem>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <SidebarMenuButton
                size='lg'
                className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
                tooltip={user?.fullname || 'User'}
                aria-label={t('navigation.userMenuFor', {
                  name: user?.fullname || 'User',
                })}
              >
                <Avatar className='h-8 w-8 rounded-lg'>
                  <AvatarFallback className='rounded-lg'>
                    {user ? getUserInitials(user.fullname) : 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className='grid flex-1 text-left text-sm leading-tight'>
                  <span className='truncate font-semibold'>
                    {user?.fullname || 'User'}
                  </span>
                  <span className='truncate text-xs'>
                    {user?.email || 'user@example.com'}
                  </span>
                </div>
                <ChevronUp className='ml-auto size-4' />
              </SidebarMenuButton>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
              side='top'
              align='end'
              sideOffset={4}
            >
              <DropdownMenuItem
                onClick={handleLogout}
                aria-label={t('auth.logoutAriaLabel')}
              >
                <span>{t('auth.logout')}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarMenuItem>
      </SidebarMenu>
      {/* Language + theme controls, moved out of the removed top header.
          Expanded: version tag on the left, icon buttons right-aligned.
          Icon mode: the stack is reversed (flex-col-reverse) so the order
          from top to bottom is language, theme, then the version chip last.
          Both icon buttons use the ghost variant so they look identical. */}
      <div className='flex items-center gap-1 px-2 pb-1 group-data-[collapsible=icon]:flex-col-reverse group-data-[collapsible=icon]:gap-1 group-data-[collapsible=icon]:px-0'>
        <div className='min-w-0 flex-1 group-data-[collapsible=icon]:flex-none'>
          <SidebarVersionInfo />
        </div>
        <div className='flex items-center gap-1 group-data-[collapsible=icon]:flex-col'>
          <LanguageSelector variant='ghost' size='icon' />
          <ThemeToggle variant='ghost' size='icon' />
        </div>
      </div>
    </SidebarFooterPrimitive>
  );
};
